import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Store,
  Check,
  Plus,
  ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Axios from "../../Common/api/axiosConfig";
import { uploadLink } from "../../utils/constants";
import {
  getActiveStoreId,
  setActiveStoreId,
} from "../../utils/storeHelper";

export interface StoreOption {
  _id: string;
  name: string;
  url: string;
  number?: number;
  visibility?: boolean;
  status?: string;
  header?: {
    logo?: string;
    store_name?: string;
  };
}

export const StoreSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [activeStore, setActiveStore] = useState<StoreOption | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const res = await Axios.get("/store/user/all");
      const storeList: StoreOption[] = res.data.stores || res.data.data || [];
      setStores(storeList);

      if (storeList.length > 0) {
        const storedId = getActiveStoreId();
        const matched = storeList.find((s) => s._id === storedId);

        if (matched) {
          setActiveStore(matched);
        } else {
          // Default to first store
          setActiveStore(storeList[0]);
          setActiveStoreId(storeList[0]._id);
        }
      }
    } catch (err) {
      console.error("Error fetching user stores:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectStore = async (store: StoreOption) => {
    if (activeStore?._id === store._id) {
      handleClose();
      return;
    }

    try {
      await Axios.post(`/store/switch/${store._id}`);
      setActiveStore(store);
      setActiveStoreId(store._id);
      handleClose();

      // Reload page to refresh all active store data across views
      window.location.reload();
    } catch (err) {
      console.error("Error switching active store:", err);
      // Fallback
      setActiveStore(store);
      setActiveStoreId(store._id);
      handleClose();
      window.location.reload();
    }
  };

  const handleCreateStore = () => {
    handleClose();
    navigate("/create-store");
  };

  if (loading && stores.length === 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
        <CircularProgress size={18} />
      </Box>
    );
  }

  if (stores.length === 0) {
    return (
      <Button
        variant="outlined"
        size="small"
        startIcon={<Plus size={16} />}
        onClick={() => navigate("/create-store")}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 2,
          fontSize: 13,
        }}
      >
        Create Store
      </Button>
    );
  }

  const activeLogo = activeStore?.header?.logo
    ? `${uploadLink}${activeStore.header.logo}`
    : null;

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Tooltip title="Switch Active Store">
        <Button
          onClick={handleOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            px: 1.5,
            py: 0.8,
            borderRadius: 2,
            border: "1px solid rgba(67, 142, 255, 0.2)",
            backgroundColor: "background.paper",
            textTransform: "none",
            color: "text.primary",
            "&:hover": {
              backgroundColor: "action.hover",
              borderColor: "primary.main",
            },
          }}
        >
          <Avatar
            src={activeLogo || undefined}
            variant="rounded"
            sx={{
              width: 28,
              height: 28,
              bgcolor: "primary.light",
              color: "primary.main",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {activeStore?.name ? activeStore.name.charAt(0).toUpperCase() : <Store size={16} />}
          </Avatar>

          <Box sx={{ textAlign: "left", maxWidth: 160, display: { xs: "none", sm: "block" } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              <Typography
                variant="body2"
                noWrap
                sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: 13 }}
              >
                {activeStore?.name || "Select Store"}
              </Typography>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: activeStore?.visibility ? "success.main" : "text.disabled",
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: 11 }}>
              {activeStore?.url || "No slug"}
            </Typography>
          </Box>

          <ChevronDown size={16} className="text-secondary" />
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 280,
            maxHeight: 400,
            borderRadius: 2.5,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            mt: 1,
          },
        }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
            Stores ({stores.length})
          </Typography>
        </Box>

        <Divider sx={{ my: 0.5 }} />

        {stores.map((store) => {
          const isSelected = activeStore?._id === store._id;
          const logo = store.header?.logo ? `${uploadLink}${store.header.logo}` : null;

          return (
            <MenuItem
              key={store._id}
              onClick={() => handleSelectStore(store)}
              selected={isSelected}
              sx={{
                py: 1.2,
                px: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                <Avatar
                  src={logo || undefined}
                  variant="rounded"
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: isSelected ? "primary.main" : "action.selected",
                    color: isSelected ? "#fff" : "text.primary",
                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  {store.name ? store.name.charAt(0).toUpperCase() : <Store size={18} />}
                </Avatar>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? "primary.main" : "text.primary",
                    }}
                  >
                    {store.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {store.url}
                  </Typography>
                </Box>
              </Box>

              {isSelected && <Check size={18} className="text-primary" style={{ flexShrink: 0 }} />}
            </MenuItem>
          );
        })}

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={handleCreateStore}
          sx={{
            py: 1.2,
            px: 2,
            color: "primary.main",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              border: "1px dashed",
              borderColor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Plus size={18} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Create New Store
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default StoreSwitcher;
